import type { CSSProperties } from "react";
import "./Skeleton.css";

type Size = number | string;
const toCss = (value: Size) =>
  typeof value === "number" ? `${value}px` : value;

export interface SkeletonProps {
  /** default "100%"; numbers are pixels */
  width?: Size;
  /** default 16; numbers are pixels */
  height?: Size;
  /** corner: sm, md (default), lg, pill or circle */
  radius?: "sm" | "md" | "lg" | "pill" | "circle";
  className?: string;
  style?: CSSProperties;
}

/**
 * One grey placeholder shape. Change width, height and radius for any
 * shape: a line of text, an avatar, a button, a card.
 * Example: <Skeleton width={120} height={12} radius="pill" />
 */
const Skeleton = ({
  width = "100%",
  height = 16,
  radius = "md",
  className = "",
  style,
}: SkeletonProps) => (
  <span
    aria-hidden="true"
    className={`ds-skeleton ds-skeleton--${radius} ${className}`.trim()}
    style={{ width: toCss(width), height: toCss(height), ...style }}
  />
);

/** Screen readers hear "Loading" once for the whole group. */
const LoadingLabel = ({ label }: { label: string }) => (
  <span className="ds-visually-hidden">{label}</span>
);

export interface SkeletonTextProps {
  /** number of lines (default 3) */
  lines?: number;
  /** height of each line (default 12) */
  lineHeight?: Size;
  /** space between lines (default 8) */
  gap?: Size;
  /** width of the last line, so it looks like a paragraph (default "60%") */
  lastLineWidth?: Size;
  /** width of the whole block (default "100%") */
  width?: Size;
  label?: string;
  className?: string;
}

/** A few lines of text. Example: <SkeletonText lines={2} /> */
export const SkeletonText = ({
  lines = 3,
  lineHeight = 12,
  gap = 8,
  lastLineWidth = "60%",
  width = "100%",
  label = "Loading",
  className = "",
}: SkeletonTextProps) => (
  <div
    role="status"
    className={`ds-skeleton-text ${className}`.trim()}
    style={{ gap: toCss(gap), width: toCss(width) }}
  >
    <LoadingLabel label={label} />
    {Array.from({ length: lines }, (_, i) => (
      <Skeleton
        key={i}
        height={lineHeight}
        radius="pill"
        width={i === lines - 1 && lines > 1 ? lastLineWidth : "100%"}
      />
    ))}
  </div>
);

export interface SkeletonListProps {
  /** number of items (default 3) */
  rows?: number;
  /** round picture at the start of each item (default true) */
  avatar?: boolean;
  /** avatar size (default 40) */
  avatarSize?: number;
  /** text lines per item (default 2) */
  lines?: number;
  /** "row" = divided rows (default), "card" = bordered cards */
  variant?: "row" | "card";
  label?: string;
  className?: string;
}

/**
 * List of items: avatar + text lines.
 * Example: <SkeletonList rows={4} variant="card" />
 */
export const SkeletonList = ({
  rows = 3,
  avatar = true,
  avatarSize = 40,
  lines = 2,
  variant = "row",
  label = "Loading",
  className = "",
}: SkeletonListProps) => (
  <div
    role="status"
    className={`ds-skeleton-group ds-skeleton-list ds-skeleton-list--${variant} ${className}`.trim()}
  >
    <LoadingLabel label={label} />
    {Array.from({ length: rows }, (_, i) => (
      <div key={i} className="ds-skeleton-list__item">
        {avatar && (
          <Skeleton width={avatarSize} height={avatarSize} radius="circle" />
        )}
        <div className="ds-skeleton-text" style={{ gap: 8, minWidth: 0 }}>
          {Array.from({ length: lines }, (_, j) => (
            <Skeleton
              key={j}
              height={j === 0 ? 14 : 12}
              radius="pill"
              width={j === 0 ? "55%" : "35%"}
            />
          ))}
        </div>
      </div>
    ))}
  </div>
);

export interface SkeletonTableProps {
  /** number of rows (default 5) */
  rows?: number;
  /** number of columns (default 5); the first is wider */
  columns?: number;
  /** round picture before the first column (default true) */
  avatar?: boolean;
  label?: string;
  className?: string;
}

/** Table rows. Example: <SkeletonTable rows={6} columns={7} /> */
export const SkeletonTable = ({
  rows = 5,
  columns = 5,
  avatar = true,
  label = "Loading",
  className = "",
}: SkeletonTableProps) => (
  <div
    role="status"
    className={`ds-skeleton-group ds-skeleton-table ${className}`.trim()}
    style={
      {
        "--ds-skeleton-columns": `2fr repeat(${Math.max(columns - 1, 0)}, 1fr)`,
      } as CSSProperties
    }
  >
    <LoadingLabel label={label} />
    {Array.from({ length: rows }, (_, i) => (
      <div key={i} className="ds-skeleton-table__row">
        {Array.from({ length: columns }, (_, j) =>
          j === 0 ? (
            <div
              key={j}
              style={{ display: "flex", alignItems: "center", gap: 12 }}
            >
              {avatar && <Skeleton width={32} height={32} radius="circle" />}
              <Skeleton height={14} radius="pill" width="70%" />
            </div>
          ) : (
            <Skeleton key={j} height={12} radius="pill" width="60%" />
          )
        )}
      </div>
    ))}
  </div>
);

export default Skeleton;
